"use client";

import { useState } from "react";
import { VStack } from "@astryxdesign/core/VStack";
import { Grid } from "@astryxdesign/core/Grid";
import { Card } from "@astryxdesign/core/Card";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { ResearchIcon } from "@/components/icons/research-icon";
import { parseLandingIcon } from "@/lib/landing/puck/icon-options";
import type { ResearchIconName } from "@/lib/icons/research-icons";
import { normalizeBackgroundImageUrl } from "@/lib/landing/media/background-image";
import { LandingCmsImage } from "@/components/landing/landing-cms-image";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import type { LandingAudienceItem } from "@/lib/landing/puck/defaults";
import {
  asLandingTextValue,
  landingTextProps,
  styledTextString,
  styledTextStyle,
} from "@/lib/landing/puck/styled-text";

type LandingAudienceBodyProps = {
  layout?: "grid" | "tabs" | "visual";
  items: LandingAudienceItem[];
};

export function LandingAudienceBody({
  layout = "grid",
  items,
}: LandingAudienceBodyProps) {
  if (layout === "tabs") {
    return <LandingAudienceTabs items={items} />;
  }

  if (layout === "visual") {
    return <LandingAudienceVisualGrid items={items} />;
  }

  return (
    <Grid columns={{ minWidth: 220, max: 3 }} gap={5} width="100%">
      {items.map((item, index) => (
        <LandingAudienceCard
          key={styledTextString(item.title)}
          item={item}
          index={index}
        />
      ))}
    </Grid>
  );
}

/** Tilda: фото-карточки 3×2 + CTA-плитка (наш accent). */
function LandingAudienceVisualGrid({ items }: { items: LandingAudienceItem[] }) {
  return (
    <Grid columns={{ minWidth: 260, max: 3 }} gap={4} width="100%">
      {items.map((item, index) =>
        item.cardStyle === "cta" ? (
          <LandingAudienceCtaTile
            key={styledTextString(item.title)}
            item={item}
            index={index}
          />
        ) : (
          <LandingAudiencePhotoTile
            key={styledTextString(item.title)}
            item={item}
            index={index}
          />
        ),
      )}
    </Grid>
  );
}

function LandingAudiencePhotoTile({
  item,
  index,
}: {
  item: LandingAudienceItem;
  index: number;
}) {
  const imageUrl = normalizeBackgroundImageUrl(item.imageUrl);

  return (
    <article className="group relative flex min-h-[17.5rem] flex-col overflow-hidden rounded-2xl shadow-sm sm:min-h-[19rem]">
      {imageUrl ? (
        <LandingCmsImage
          src={imageUrl}
          alt={styledTextString(item.title)}
          width={640}
          height={800}
          fill
          sizes="(max-width: 640px) 100vw, 33vw"
          className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      ) : (
        <div className="absolute inset-0 bg-[var(--color-surface-muted)]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />

      <div className="relative flex h-full flex-col justify-between p-5 sm:p-6">
        <LandingCanvasText
          field={`items.${index}.title`}
          value={item.title}
          label="Заголовок"
        >
          <Heading
            level={3}
            className="text-white drop-shadow-sm"
            {...landingTextProps(item.title)}
          />
        </LandingCanvasText>

        <LandingCanvasText
          field={`items.${index}.text`}
          value={item.text}
          label="Текст"
          multiline
          hideIfEmpty
        >
          <div className="mt-auto max-w-full rounded-xl bg-black/45 px-4 py-3 backdrop-blur-sm">
            <Text
              size="sm"
              className="leading-snug text-white/95"
              {...landingTextProps(item.text)}
            />
          </div>
        </LandingCanvasText>
      </div>
    </article>
  );
}

function LandingAudienceCtaTile({
  item,
  index,
}: {
  item: LandingAudienceItem;
  index: number;
}) {
  return (
    <Card
      padding={0}
      className="flex min-h-[17.5rem] flex-col overflow-hidden rounded-2xl border border-border bg-[var(--color-surface-muted)] sm:min-h-[19rem]"
    >
      <VStack gap={5} align="start" width="100%" className="h-full p-5 sm:p-6">
        <LandingCanvasText
          field={`items.${index}.title`}
          value={item.title}
          label="Заголовок"
        >
          <Heading level={3} {...landingTextProps(item.title)} />
        </LandingCanvasText>

        <div className="mt-auto w-full">
          <div className="rounded-2xl border border-border bg-[var(--color-background-body)] px-5 py-4 shadow-sm">
            <LandingCanvasText
              field={`items.${index}.text`}
              value={item.text}
              label="Текст"
              multiline
              hideIfEmpty
            >
              <Text color="secondary" className="leading-relaxed" {...landingTextProps(item.text)} />
            </LandingCanvasText>
            <LandingCanvasText
              field={`items.${index}.ctaLabel`}
              value={asLandingTextValue(item.ctaLabel)}
              label="Кнопка"
              inline
              hideIfEmpty
            >
              {styledTextString(item.ctaLabel) && item.ctaHref ? (
                <a
                  href={item.ctaHref}
                  className="mt-2 inline-block font-semibold text-[var(--color-text-accent)] underline-offset-2 hover:underline"
                  style={styledTextStyle(item.ctaLabel)}
                >
                  {styledTextString(item.ctaLabel)}
                </a>
              ) : null}
            </LandingCanvasText>
          </div>
        </div>
      </VStack>
    </Card>
  );
}

/** Tilda: вертикальные табы слева, крупная карточка справа. */
function LandingAudienceTabs({ items }: { items: LandingAudienceItem[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const safeIndex = Math.min(Math.max(0, activeIndex), items.length - 1);
  const active = items[safeIndex];

  if (!active) return null;

  return (
    <Grid columns={{ minWidth: 280, max: 2 }} gap={6} width="100%" className="items-stretch">
      <VStack gap={2} align="stretch" width="100%" className="min-w-0">
        {items.map((item, index) => {
          const isActive = index === safeIndex;
          return (
            <button
              key={styledTextString(item.title)}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={
                isActive
                  ? "rounded-xl border border-[var(--color-border-accent)] bg-[var(--color-surface-accent-muted)] px-4 py-3 text-left text-sm font-semibold text-[var(--color-text-primary)] transition-colors"
                  : "rounded-xl border border-transparent px-4 py-3 text-left text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border-default)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)]"
              }
              style={styledTextStyle(item.title)}
            >
              {styledTextString(item.title)}
            </button>
          );
        })}
      </VStack>

      <Card padding={8} className="h-full min-w-0">
        <VStack gap={5} align="start" width="100%">
          <ResearchIcon
            name={parseLandingIcon(active.icon) as ResearchIconName}
            size="lg"
          />
          <VStack gap={3} align="start" maxWidth={560}>
            <LandingCanvasText
              field={`items.${safeIndex}.title`}
              value={active.title}
              label="Заголовок"
            >
              <Heading level={3} {...landingTextProps(active.title)} />
            </LandingCanvasText>
            <LandingCanvasText
              field={`items.${safeIndex}.text`}
              value={active.text}
              label="Текст"
              multiline
            >
              <Text type="large" color="secondary" {...landingTextProps(active.text)} />
            </LandingCanvasText>
          </VStack>
          <LandingCanvasText
            field={`items.${safeIndex}.ctaLabel`}
            value={asLandingTextValue(active.ctaLabel)}
            label="Кнопка"
            inline
            hideIfEmpty
          >
            {styledTextString(active.ctaLabel) && active.ctaHref ? (
              <a href={active.ctaHref}>
                <Button
                  label={styledTextString(active.ctaLabel)}
                  variant="primary"
                  size="lg"
                  style={styledTextStyle(active.ctaLabel)}
                />
              </a>
            ) : null}
          </LandingCanvasText>
        </VStack>
      </Card>
    </Grid>
  );
}

function LandingAudienceCard({
  item,
  index,
}: {
  item: LandingAudienceItem;
  index: number;
}) {
  return (
    <Card padding={6}>
      <VStack gap={3} align="start">
        <ResearchIcon
          name={parseLandingIcon(item.icon) as ResearchIconName}
          size="md"
        />
        <LandingCanvasText
          field={`items.${index}.title`}
          value={item.title}
          label="Заголовок"
        >
          <Heading level={3} {...landingTextProps(item.title)} />
        </LandingCanvasText>
        <LandingCanvasText
          field={`items.${index}.text`}
          value={item.text}
          label="Текст"
          multiline
        >
          <Text color="secondary" {...landingTextProps(item.text)} />
        </LandingCanvasText>
      </VStack>
    </Card>
  );
}
